// Step 3 K2c2 (#1844): the platform Components release card. Cleo asks to publish the platform's Components
// (neucleos_canvas component-release-publish {releaseKey, builds, digest}); the card reads the World's own review of
// those exact builds (component-release-review), shows its lines verbatim and Added, Changed and Kept by name, and
// Approve waits until the review's digest is the request's. The World approves this step only from a Desk that shows
// this card (backend lib/gateCatalog.js CARD_APPROVED_STEP_KEYS, component-release-card-v1).
import { canvasDoor } from "./canvasShareClient";

export const COMPONENT_RELEASE_ACTION = "component-release-publish";

/** The exact release the call names: what the card asks the World about. */
export interface ComponentReleaseRef {
  readonly releaseKey: string;
  readonly digest: string;
  readonly builds: ReadonlyArray<{
    readonly slot: string;
    readonly componentId: string;
    readonly version: number;
  }>;
}
export type ComponentReleaseRead =
  | { readonly status: "reading" }
  | {
      readonly status: "loaded";
      readonly releaseKey: string;
      readonly lines: ReadonlyArray<string>;
      readonly added: ReadonlyArray<string>;
      readonly changed: ReadonlyArray<string>;
      readonly kept: ReadonlyArray<string>;
      readonly digest: string;
    }
  // `refusal`: the World's own reason, when it answered and refused (not when it could not be reached).
  | { readonly status: "failed"; readonly reason: string; readonly refusal?: string };

export const COMPONENT_RELEASE_UNREAD =
  "Neucleos could not read this release's card, so it cannot be approved here.";
export const COMPONENT_RELEASE_MOVED = "This release changed since Cleo asked; ask her again.";

const RELEASE_KEY = /^pcr_[0-9a-f]{32}$/;
const DIGEST = /^[0-9a-f]{64}$/;
/** The release a component-release-publish call names, or null when this card cannot show it exactly. */
export function componentReleaseRefOf(input: Record<string, unknown>): ComponentReleaseRef | null {
  const { releaseKey, digest, builds } = input;
  if (typeof releaseKey !== "string" || !RELEASE_KEY.test(releaseKey)) return null;
  if (typeof digest !== "string" || !DIGEST.test(digest)) return null;
  if (!Array.isArray(builds) || builds.length < 1) return null;
  const rows: Array<ComponentReleaseRef["builds"][number]> = [];
  for (const build of builds) {
    if (typeof build !== "object" || build === null) return null;
    const row = build as Record<string, unknown>;
    const version = row["version"];
    if (
      typeof row["slot"] !== "string" ||
      typeof row["componentId"] !== "string" ||
      typeof version !== "number" ||
      !Number.isSafeInteger(version)
    )
      return null;
    rows.push({ slot: row["slot"], componentId: row["componentId"], version });
  }
  return { releaseKey, digest, builds: rows };
}

// A name or line that breaks across lines could read as another line of the card (#1844 Codex pass 1 NIT), so a review
// carrying one is not shown and cannot be approved here.
const BREAK = /[\r\n\u0085\u2028\u2029]/;
const namesOf = (value: unknown): string[] | null =>
  Array.isArray(value) &&
  value.every(
    (entry) =>
      typeof entry === "object" &&
      entry !== null &&
      typeof (entry as Record<string, unknown>)["name"] === "string" &&
      !BREAK.test((entry as Record<string, string>)["name"]!),
  )
    ? value.map((entry) => (entry as Record<string, string>)["name"]!)
    : null;

/** The World's review of these exact builds: its lines, Added, Changed and Kept by name, and the card's digest. */
export async function readComponentRelease(
  worldKey: string,
  ref: ComponentReleaseRef,
): Promise<ComponentReleaseRead> {
  const read = await canvasDoor(worldKey, "component-release-review", { builds: ref.builds });
  if (!read.ok)
    return read.reason
      ? {
          status: "failed",
          reason: `${COMPONENT_RELEASE_UNREAD} ${read.reason}`,
          refusal: read.reason,
        }
      : { status: "failed", reason: COMPONENT_RELEASE_UNREAD };
  const card = read.body["card"] as Record<string, unknown> | null | undefined;
  if (typeof card !== "object" || card === null)
    return { status: "failed", reason: COMPONENT_RELEASE_UNREAD };
  const digest = card["digest"];
  const lines = card["lines"];
  const added = namesOf(card["added"]);
  const changed = namesOf(card["changed"]);
  const kept = namesOf(card["kept"]);
  if (
    typeof digest !== "string" ||
    typeof card["releaseKey"] !== "string" ||
    !Array.isArray(lines) ||
    lines.length === 0 ||
    lines.some((line) => typeof line !== "string" || BREAK.test(line)) ||
    !added ||
    !changed ||
    !kept
  )
    return { status: "failed", reason: COMPONENT_RELEASE_UNREAD };
  return {
    status: "loaded",
    releaseKey: card["releaseKey"],
    lines: lines as string[],
    added,
    changed,
    kept,
    digest,
  };
}

/**
 * A re-read of a card already shown (#1844 Codex pass 1): once the World has answered this release's review, its later
 * refusal of the same builds means the release moved under the open card (a later deploy proposed another, a build or
 * the Library changed), so the card says so and Approve stays off. A read that could not reach the World says only that.
 */
export function componentReleaseAfter(
  shown: ComponentReleaseRead | null,
  next: ComponentReleaseRead,
): ComponentReleaseRead {
  if (next.status !== "failed" || next.refusal === undefined || shown === null) return next;
  const moved =
    shown.status === "loaded" ||
    (shown.status === "failed" && shown.reason.startsWith(COMPONENT_RELEASE_MOVED));
  return moved
    ? {
        status: "failed",
        reason: `${COMPONENT_RELEASE_MOVED} ${next.refusal}`,
        refusal: next.refusal,
      }
    : next;
}

/** Why the card cannot be approved as read, or null: the review must be this release, at the request's digest. */
export function componentReleaseBlocked(
  ref: ComponentReleaseRef,
  read: ComponentReleaseRead,
): string | null {
  if (read.status === "reading") return "Reading this release's card…";
  if (read.status === "failed") return read.reason;
  if (read.releaseKey !== ref.releaseKey || read.digest !== ref.digest)
    return COMPONENT_RELEASE_MOVED;
  return null;
}
